import {
  CALL_RING_TIMEOUT_MS,
  type CallDeviceInfo,
  type CallDto,
  type CallEndReason,
  type CallMedia,
  type CallQualityReport,
  type CallReport,
  type IceServersDto,
  type ServerEvent,
  type UserSummaryDto,
} from '@dijitalapp/shared';
import { api } from './api';
import { thisDevice } from './device';
import { uuidv7 } from './outbox';
import type { RealtimeClient } from './realtime';
import { loadCustomBackground, loadSegmenter, presetBackground, saveCustomBackground, type BackgroundChoice, type BackgroundPreset } from './backgrounds';
import { VideoEffectProcessor, type BackgroundLayer, type VideoEffect } from './video-effects';
import { addMissedCall, closeLocal, flashTitle, holdSoundsForCall, lookingElsewhere, ringtone, showLocal } from './notify';

/**
 * 1:1 WebRTC calls (ADR-015). The browser does all media and DTLS-SRTP encryption; this class only runs
 * the offer/answer exchange over the realtime gateway and keeps the UI state.
 */

export type CallPhase = 'outgoing' | 'incoming' | 'connecting' | 'active' | 'ended';
/** `no_media`: answered, but sound and picture could not get through (network blocks the call's traffic). */
export type CallFailure = CallEndReason | 'permission' | 'unsupported' | 'not_allowed' | 'no_media';

/** After answering, media must flow within this time; otherwise the call ends with a clear reason. */
const CONNECT_TIMEOUT_MS = 30_000;
/** A path lost mid-call: this long "disconnected" before the connection is restarted (it often comes back alone). */
const DISCONNECTED_GRACE_MS = 3000;
/** Restarts at most this often, and give up (call ends "failed") after this long without getting back. */
const RESTART_GAP_MS = 4000;
const RECONNECT_GIVE_UP_MS = 45_000;
/** An offer that got no answer (the socket was down) is withdrawn after this long, so the next try can go. */
const OFFER_WAIT_MS = 8000;
/** Audio-flow checks while connected: no sound coming in (or going out) for this long means something is stuck. */
const WATCHDOG_MS = 2000;
const NO_AUDIO_MS = 5000;
const SILENT_BANNER_MS = 9000;
const RECOVERY_GAP_MS = 10_000;

/** The call's sound should be (re)started: the voice element calls play() again (lost on some iPhones). */
export const CALL_AUDIO_KICK_EVENT = 'dijitalapp:call-audio-kick';

type Snapshot = Omit<CallQualityReport, 'iceRestarts' | 'noAudioDetected' | 'device' | 'durationMs'>;

/** One reading of a connection's audio statistics (packets in, bytes out) and of the path media takes. */
export async function audioStats(pc: RTCPeerConnection): Promise<{ inPackets: number; outBytes: number; snapshot: Snapshot }> {
  const stats = await pc.getStats();
  const byId = new Map<string, Record<string, unknown>>();
  stats.forEach((v: Record<string, unknown>) => byId.set(String(v['id']), v));
  let inPackets = 0;
  let outBytes = 0;
  let lost: number | null = null;
  let jitter: number | null = null;
  let pair: Record<string, unknown> | undefined;
  for (const v of byId.values()) {
    const kind = v['kind'] ?? v['mediaType'];
    if (v['type'] === 'inbound-rtp' && kind === 'audio') {
      inPackets += Number(v['packetsReceived'] ?? 0);
      lost = (lost ?? 0) + Math.max(0, Number(v['packetsLost'] ?? 0));
      if (typeof v['jitter'] === 'number') jitter = Math.max(jitter ?? 0, Math.round(v['jitter'] * 1000));
    } else if (v['type'] === 'outbound-rtp' && kind === 'audio') {
      outBytes += Number(v['bytesSent'] ?? 0);
    } else if (v['type'] === 'transport' && typeof v['selectedCandidatePairId'] === 'string') {
      pair = byId.get(v['selectedCandidatePairId']);
    }
  }
  if (!pair) for (const v of byId.values()) if (v['type'] === 'candidate-pair' && v['state'] === 'succeeded' && (v['nominated'] || v['selected'])) pair = v;
  const local = pair ? byId.get(String(pair['localCandidateId'])) : undefined;
  const remote = pair ? byId.get(String(pair['remoteCandidateId'])) : undefined;
  const lt = String(local?.['candidateType'] ?? '');
  const rt = String(remote?.['candidateType'] ?? '');
  const relayed = lt === 'relay' || rt === 'relay';
  const type = relayed ? 'relay' : CANDIDATE_TYPES.includes(lt) ? (lt as CandidateType) : null;
  const via = String((lt === 'relay' ? local?.['relayProtocol'] : local?.['protocol']) ?? '');
  const rtt = typeof pair?.['currentRoundTripTime'] === 'number' ? Math.round((pair['currentRoundTripTime'] as number) * 1000) : null;
  return {
    inPackets,
    outBytes,
    snapshot: {
      candidateType: type,
      protocol: via === 'udp' || via === 'tcp' || via === 'tls' ? via : null,
      rttMs: rtt === null ? null : Math.min(60_000, rtt),
      packetsLost: lost,
      jitterMs: jitter === null ? null : Math.min(60_000, jitter),
      audioPacketsReceived: inPackets,
      audioBytesSent: outBytes,
    },
  };
}

type CandidateType = CallReport['localTypes'][number];
const CANDIDATE_TYPES: readonly string[] = ['host', 'srflx', 'prflx', 'relay'];

/** Candidate kinds, relay transports and the pair in use, read from the connection's own statistics. */
async function connectionFacts(pc: RTCPeerConnection): Promise<Pick<CallReport, 'localTypes' | 'remoteTypes' | 'relayProtocols' | 'selected'>> {
  const local = new Set<CandidateType>();
  const remote = new Set<CandidateType>();
  const relay = new Set<'udp' | 'tcp' | 'tls'>();
  let selected: CallReport['selected'] = null;
  try {
    const stats = await pc.getStats();
    const byId = new Map<string, Record<string, unknown>>();
    stats.forEach((v: Record<string, unknown>) => byId.set(String(v['id']), v));
    for (const v of byId.values()) {
      const type = String(v['candidateType'] ?? '');
      if (!CANDIDATE_TYPES.includes(type)) continue;
      if (v['type'] === 'local-candidate') {
        local.add(type as CandidateType);
        const via = String(v['relayProtocol'] ?? '');
        if (type === 'relay' && (via === 'udp' || via === 'tcp' || via === 'tls')) relay.add(via);
      } else if (v['type'] === 'remote-candidate') remote.add(type as CandidateType);
    }
    for (const v of byId.values()) {
      if (v['type'] !== 'candidate-pair' || v['state'] !== 'succeeded' || !(v['nominated'] || v['selected'])) continue;
      const l = byId.get(String(v['localCandidateId']));
      const r = byId.get(String(v['remoteCandidateId']));
      const lt = String(l?.['candidateType'] ?? '');
      const rt = String(r?.['candidateType'] ?? '');
      const proto = String(l?.['protocol'] ?? 'udp');
      if (CANDIDATE_TYPES.includes(lt) && CANDIDATE_TYPES.includes(rt)) {
        selected = { local: lt as CandidateType, remote: rt as CandidateType, protocol: proto === 'tcp' ? 'tcp' : 'udp' };
      }
    }
  } catch {
    /* statistics are best effort */
  }
  return { localTypes: [...local], remoteTypes: [...remote], relayProtocols: [...relay], selected };
}

/**
 * Checks this device's route to the call relay (TURN): asks for a relay address over each transport the
 * server offers. Used by the admin "test calls" button; nothing is connected to anyone.
 */
export async function probeCallRelay(): Promise<{ urls: string[]; results: { url: string; relay: boolean; ms: number }[]; srflx: boolean }> {
  const ice = await api<IceServersDto>('GET', '/calls/ice-servers');
  // Every relay is tried (ours and, when switched on, Cloudflare's), one address at a time.
  const relays = ice.iceServers.filter((s) => s.username && s.credential);
  const gather = (servers: RTCIceServer[], policy: RTCIceTransportPolicy) =>
    new Promise<{ types: Set<string>; ms: number }>((resolve) => {
      const started = performance.now();
      const pc = new RTCPeerConnection({ iceServers: servers, iceTransportPolicy: policy });
      const types = new Set<string>();
      const done = () => {
        pc.close();
        resolve({ types, ms: Math.round(performance.now() - started) });
      };
      const timer = setTimeout(done, 8000);
      pc.onicecandidate = (e) => {
        if (e.candidate?.type) types.add(e.candidate.type);
        if (!e.candidate) {
          clearTimeout(timer);
          done();
        }
      };
      pc.createDataChannel('probe');
      void pc.createOffer().then((o) => pc.setLocalDescription(o));
    });
  const results: { url: string; relay: boolean; ms: number }[] = [];
  for (const relay of relays) {
    for (const url of Array.isArray(relay.urls) ? relay.urls : [relay.urls]) {
      const r = await gather([{ urls: [url], username: relay.username!, credential: relay.credential! }], 'relay');
      results.push({ url, relay: r.types.has('relay'), ms: r.ms });
    }
  }
  const stun = ice.iceServers.filter((s) => !s.username);
  const s = stun.length ? await gather(stun, 'all') : { types: new Set<string>() };
  return { urls: ice.iceServers.flatMap((x) => x.urls), results, srflx: s.types.has('srflx') };
}

export interface CallView {
  /** The other person's device is ringing (outgoing calls): "Ringing" rather than "Calling". */
  ringing?: boolean;
  callId: string;
  conversationId: string;
  peer: Pick<UserSummaryDto, 'id' | 'displayName'>;
  media: CallMedia;
  phase: CallPhase;
  muted: boolean;
  cameraOff: boolean;
  /** Time the media connected (for the call timer). */
  connectedAt: number | null;
  endReason: CallFailure | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  /** Live effect on my camera picture. */
  effect: VideoEffect;
  /** The camera can blur its own background (some devices/browsers), and whether that is on. */
  deviceBlur: { available: boolean; on: boolean };
  /** Output device for the other side's voice (where the browser lets pages choose). */
  speakerId: string | null;
  /** What is behind me in the picture, and whether the person detection is ready. */
  background: BackgroundChoice;
  backgroundStatus: 'idle' | 'loading' | 'ready' | 'failed';
  /** My screen (or window/tab) going out instead of the camera, while I share it. */
  screenStream: MediaStream | null;
  /** The connection can carry a screen (false only when the other side runs an older app). */
  canShare: boolean;
  /** What the other side reports about itself over the call's own encrypted channel. */
  peerState: PeerState;
  /** What the other person is on (phone, tablet, computer; app or browser), when their app said. */
  peerDevice: CallDeviceInfo | null;
  /** The path was lost (network change, weak signal) and the connection is being restored. */
  reconnecting: boolean;
  /** Connected, but no sound has come in for a while even after a recovery try: the screen offers a tap. */
  silent: boolean;
  /** The other side's voice alone (one stream per connection: played by one audio element, set once). */
  remoteAudio: MediaStream | null;
  /** The other person's volume for me (0–1, where the browser allows it). */
  peerVolume: number;
}

export interface PeerState {
  muted: boolean;
  cameraOff: boolean;
  sharing: boolean;
}

const NO_PEER_STATE: PeerState = { muted: false, cameraOff: false, sharing: false };

/** Remembered on this device between calls. */
export interface CallSettings {
  micId: string | null;
  camId: string | null;
  speakerId: string | null;
  noiseSuppression: boolean;
  echoCancellation: boolean;
  autoGainControl: boolean;
  effect: VideoEffect;
  deviceBlur: boolean;
  background: BackgroundChoice;
}

const SETTINGS_KEY = 'da.callSettings';
const DEFAULT_SETTINGS: CallSettings = {
  micId: null,
  camId: null,
  speakerId: null,
  noiseSuppression: true,
  echoCancellation: true,
  autoGainControl: true,
  effect: 'none',
  deviceBlur: false,
  background: 'none',
};

export function loadCallSettings(): CallSettings {
  try {
    return { ...DEFAULT_SETTINGS, ...(JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? '{}') as Partial<CallSettings>) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function saveCallSettings(s: CallSettings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch {
    /* storage unavailable: settings last for this call only */
  }
}

type BlurCapable = MediaTrackCapabilities & { backgroundBlur?: boolean[] };

export function callsSupported(): boolean {
  return typeof RTCPeerConnection !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
}

/** "Back to call" from a chat: the shrunk call window (1:1 or group) opens full again. */
export const CALL_EXPAND_EVENT = 'dijitalapp:call-expand';
export function expandCallScreen(): void {
  window.dispatchEvent(new Event(CALL_EXPAND_EVENT));
}

/** Desktop browsers can share a screen, window or tab; phone browsers cannot. */
export function screenShareSupported(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function';
}

/** Mixes the microphone with the sound of the shared tab or screen into one outgoing track. */
interface AudioMix {
  ctx: AudioContext;
  display: MediaStreamTrack;
  out: MediaStreamTrack;
}

/** My call running on another of my devices, which this one can take over. */
export interface CallElsewhere {
  callId: string;
  conversationId: string;
  peer: CallView['peer'];
  media: CallMedia;
}

export class CallManager {
  view: CallView | null = null;
  elsewhere: CallElsewhere | null = null;
  /** Set by the store: in a group call (then incoming 1:1 calls are declined as busy). */
  groupBusy: () => boolean = () => false;
  /** Set by the store: continue as a group call in this group (after people were added to the call). */
  onUpgrade: (to: { conversationId: string; title: string; media: CallMedia }, carry: { muted: boolean; cameraOff: boolean }) => void = () => undefined;

  /** Adds people to this 1:1 call: it continues as a group call in a new group with everyone. */
  async addPeople(userIds: string[]): Promise<void> {
    const view = this.view;
    if (!view || view.phase !== 'active') return;
    const to = await api<{ conversationId: string; title: string; media: CallMedia }>('POST', `/calls/${view.callId}/add`, { userIds });
    this.upgrade(view.callId, to);
  }

  /** Lets go of the 1:1 call without the "call ended" screen, and joins the group call instead. */
  private upgrade(callId: string, to: { conversationId: string; title: string; media: CallMedia }) {
    if (this.view?.callId !== callId || this.view.phase === 'ended') return;
    // Microphone and camera stay as they were (off stays off) in the group call.
    const carry = { muted: this.view.muted, cameraOff: this.view.cameraOff };
    this.finish('hangup');
    if (this.clearTimer) clearTimeout(this.clearTimer);
    this.view = null;
    this.listeners.forEach((l) => l());
    this.onUpgrade(to, carry);
  }
  /** The call this device is taking over (its own "moved" note is not for it). */
  private moving: string | null = null;
  private pc: RTCPeerConnection | null = null;
  private pendingIce: RTCIceCandidateInit[] = [];
  private incomingOffer: string | null = null;
  private listeners = new Set<() => void>();
  private clearTimer: ReturnType<typeof setTimeout> | null = null;
  private ringer: { stop: () => void } | null = null;
  settings: CallSettings = loadCallSettings();
  /** The camera track as it comes from the device (the sent one may be an effect canvas). */
  private camTrack: MediaStreamTrack | null = null;
  private processor: VideoEffectProcessor | null = null;
  private bitmaps = new Map<string, ImageBitmap>();
  /** Negotiated before the offer, so it needs no extra signalling; carries mute/camera/sharing state. */
  private channel: RTCDataChannel | null = null;
  /** The remote side's tracks, gathered into one stream however the browser groups them. */
  private remote: MediaStreamTrack[] = [];
  private mix: AudioMix | null = null;
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectStarted = 0;
  private relayOnly = false;
  private reported = false;
  /** Perfect negotiation: on crossing restart offers the polite side (the one that answered) gives way. */
  private polite = false;
  private negotiating = false;
  private restarts = 0;
  private lastRestart = 0;
  private reconnectingSince = 0;
  private everConnected = false;
  private disconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private offerTimer: ReturnType<typeof setTimeout> | null = null;
  private watchdog: ReturnType<typeof setInterval> | null = null;
  private audioIn = { value: 0, at: 0 };
  private audioOut = { value: 0, at: 0 };
  private lastRecovery = 0;
  private noAudioSeen = false;
  private snapshot: Snapshot | null = null;
  private micPending = false;
  private holding = false;

  /** Stops a ring the server has given up on, even if its "ended" never reached this tab. */
  private ringTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly rt: RealtimeClient) {
    // A tab that slept or lost its connection may have missed "call.ended": ask before ringing on.
    rt.onState((s) => {
      if (s === 'connected') {
        void this.checkStillRinging();
        void this.checkStillOn();
      }
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void this.checkStillRinging();
        void this.checkStillOn();
        void this.checkElsewhere();
        // iPhones end the microphone while the app is in the background: take it again on return.
        const mic = this.view?.localStream?.getAudioTracks()[0];
        if (this.view && this.view.phase !== 'ended' && (this.micPending || mic?.readyState === 'ended')) void this.reacquireMic();
        if (this.view?.phase === 'active') window.dispatchEvent(new Event(CALL_AUDIO_KICK_EVENT));
      }
    });
    rt.onState((s) => {
      if (s !== 'connected') return;
      void this.checkElsewhere();
      // Signalling sent while the socket was down is lost: restart a connection that is not healthy.
      const pc = this.pc;
      if (pc && this.view?.phase === 'active' && (this.view.reconnecting || pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed')) this.networkChanged();
    });
    // Wi-Fi to mobile data and back: the old path is gone; a fresh one is found at once rather than after a timeout.
    window.addEventListener('online', () => this.networkChanged());
    (navigator as Navigator & { connection?: EventTarget }).connection?.addEventListener?.('change', () => this.networkChanged());
  }

  private networkChanged(): void {
    if (!this.pc || this.view?.phase !== 'active') return;
    this.lastRestart = 0;
    setTimeout(() => void this.restartIce(), 1000);
  }

  /** The call's media started here: our own sounds step aside (iPhone audio session), once per call. */
  private hold(on: boolean): void {
    if (this.holding === on) return;
    this.holding = on;
    holdSoundsForCall(on);
  }

  /** The microphone track stopped (iPhone background, another app took the sound): taken again when possible. */
  private watchMic(track: MediaStreamTrack): void {
    track.addEventListener('ended', () => {
      if (!this.view || this.view.phase === 'ended' || this.view.localStream?.getAudioTracks()[0] !== track) return;
      if (document.visibilityState === 'visible') void this.reacquireMic();
      else this.micPending = true;
    });
  }

  private async reacquireMic(): Promise<void> {
    this.micPending = false;
    const view = this.view;
    if (!view?.localStream || view.phase === 'ended' || view.phase === 'incoming') return;
    try {
      const fresh = await navigator.mediaDevices.getUserMedia({ audio: this.audioConstraints() });
      const track = fresh.getAudioTracks()[0];
      if (!track) return;
      if (this.view?.callId !== view.callId || this.view.phase === 'ended') {
        track.stop();
        return;
      }
      await this.swapTrack('audio', track);
    } catch {
      this.micPending = true; // tried again when the app is back in front
    }
  }

  /**
   * A fresh path for the running call (ICE restart): a new offer over the same connection, through the
   * server; the call, its timer and its encryption go on. Never more often than RESTART_GAP_MS.
   */
  private async restartIce(): Promise<void> {
    const pc = this.pc;
    const view = this.view;
    if (!pc || !view || view.phase !== 'active' || pc.signalingState === 'closed') return;
    const now = Date.now();
    if (now - this.lastRestart < RESTART_GAP_MS || pc.signalingState !== 'stable') return;
    this.lastRestart = now;
    this.restarts++;
    if (!view.reconnecting) this.reconnectingSince = now;
    this.update({ reconnecting: true });
    try {
      const offer = await pc.createOffer({ iceRestart: true });
      if (this.pc !== pc || pc.signalingState !== 'stable') return;
      await pc.setLocalDescription(offer);
      this.rt.command('call.renegotiate', { callId: view.callId, sdp: offer.sdp ?? '' });
      if (this.offerTimer) clearTimeout(this.offerTimer);
      // No answer (signalling lost): withdraw the offer so the next try can be made.
      this.offerTimer = setTimeout(() => {
        if (this.pc === pc && pc.signalingState === 'have-local-offer') void pc.setLocalDescription({ type: 'rollback' }).catch(() => undefined);
      }, OFFER_WAIT_MS);
    } catch {
      /* tried again on the next change or check */
    }
  }

  /** The other side restarts the connection: answer its offer (on crossing offers, the polite side gives way). */
  private async onRenegotiate(callId: string, sdp: string): Promise<void> {
    const pc = this.pc;
    const view = this.view;
    if (!pc || view?.callId !== callId || view.phase === 'ended' || view.phase === 'incoming' || view.phase === 'outgoing') return;
    if (pc.signalingState === 'have-local-offer' && !this.polite) return; // mine goes first; they answer it
    this.negotiating = true;
    try {
      if (pc.signalingState === 'have-local-offer') await pc.setLocalDescription({ type: 'rollback' });
      await pc.setRemoteDescription({ type: 'offer', sdp });
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      this.rt.command('call.renegotiate_answer', { callId, sdp: answer.sdp ?? '' });
    } catch {
      /* the next restart tries again */
    } finally {
      this.negotiating = false;
      await this.flushIce();
    }
  }

  private async onRenegotiated(callId: string, sdp: string): Promise<void> {
    const pc = this.pc;
    if (!pc || this.view?.callId !== callId || pc.signalingState !== 'have-local-offer') return;
    if (this.offerTimer) clearTimeout(this.offerTimer);
    this.offerTimer = null;
    this.negotiating = true;
    try {
      await pc.setRemoteDescription({ type: 'answer', sdp });
      // A restart over a path that still worked never leaves "connected": nothing else would clear the state.
      this.clearReconnecting(pc);
    } catch {
      /* the next restart tries again */
    } finally {
      this.negotiating = false;
      await this.flushIce();
    }
  }

  private clearReconnecting(pc: RTCPeerConnection): void {
    const ice = pc.iceConnectionState;
    if (this.pc === pc && this.view?.reconnecting && pc.signalingState === 'stable' && (ice === 'connected' || ice === 'completed')) this.update({ reconnecting: false });
  }

  /** Every WATCHDOG_MS while connected: is sound coming in and going out? Recovers what it can. */
  private startWatchdog(): void {
    if (this.watchdog) return;
    const now = Date.now();
    this.audioIn = { value: 0, at: now };
    this.audioOut = { value: 0, at: now };
    this.watchdog = setInterval(() => void this.check(), WATCHDOG_MS);
  }

  private stopWatchdog(): void {
    if (this.watchdog) clearInterval(this.watchdog);
    this.watchdog = null;
  }

  private async check(): Promise<void> {
    const pc = this.pc;
    const view = this.view;
    if (!pc || !view || view.phase !== 'active') return;
    const now = Date.now();
    if (view.reconnecting && this.reconnectingSince && now - this.reconnectingSince > RECONNECT_GIVE_UP_MS) return void this.failMedia(pc, 'ice_failed');
    let reading: Awaited<ReturnType<typeof audioStats>>;
    try {
      reading = await audioStats(pc);
    } catch {
      return;
    }
    if (this.pc !== pc || this.view?.phase !== 'active') return;
    this.clearReconnecting(pc);
    const current = this.view;
    this.snapshot = reading.snapshot;
    // Counters moved (or started again on a new connection after a move).
    if (reading.inPackets !== this.audioIn.value) this.audioIn = { value: reading.inPackets, at: now };
    if (reading.outBytes !== this.audioOut.value) this.audioOut = { value: reading.outBytes, at: now };
    // Their microphone is off on purpose: not a fault (and a grace period once it comes back on).
    if (current.peerState.muted || current.reconnecting) this.audioIn.at = now;
    if (current.muted || current.reconnecting) this.audioOut.at = now;
    const inStalled = now - this.audioIn.at >= NO_AUDIO_MS;
    const outStalled = now - this.audioOut.at >= NO_AUDIO_MS;
    if (inStalled) this.noAudioSeen = true;
    if ((inStalled || outStalled) && now - this.lastRecovery > RECOVERY_GAP_MS) {
      this.lastRecovery = now;
      const mic = current.localStream?.getAudioTracks()[0];
      if (outStalled && (!mic || mic.readyState === 'ended')) void this.reacquireMic();
      else {
        this.lastRestart = 0;
        void this.restartIce();
      }
      window.dispatchEvent(new Event(CALL_AUDIO_KICK_EVENT));
    }
    const silent = now - this.audioIn.at >= SILENT_BANNER_MS;
    if (silent !== current.silent) this.update({ silent });
  }

  /** "No sound — tap": the voice is started again (the tap also lets iPhones play) and the path renewed. */
  recoverAudio(): void {
    window.dispatchEvent(new Event(CALL_AUDIO_KICK_EVENT));
    const mic = this.view?.localStream?.getAudioTracks()[0];
    if (mic?.readyState === 'ended') void this.reacquireMic();
    this.lastRestart = 0;
    void this.restartIce();
  }

  setPeerVolume(v: number): void {
    this.update({ peerVolume: Math.min(1, Math.max(0, v)) });
  }

  /** What this device measured over the call, sent once at its end (no addresses). */
  private sendQuality(callId: string, connectedAt: number | null): void {
    const snap = this.snapshot;
    if (!snap || !connectedAt) return;
    const body: CallQualityReport = {
      ...snap,
      iceRestarts: Math.min(1000, this.restarts),
      noAudioDetected: this.noAudioSeen,
      device: thisDevice(),
      durationMs: Math.max(0, Date.now() - connectedAt),
    };
    void api('POST', `/calls/${callId}/quality`, body).catch(() => undefined);
  }

  /**
   * The call shown here still running? Asked after the socket reconnects, when the app comes back to the
   * front, and when the other side's connection closes: an "ended" missed while offline must not leave a
   * finished call open.
   */
  private async checkStillOn(): Promise<void> {
    const view = this.view;
    if (!view || view.phase === 'ended' || view.phase === 'incoming') return;
    try {
      const { calls } = await api<{ calls: CallDto[] }>('GET', `/calls?conversationId=${view.conversationId}`);
      const call = calls.find((c) => c.id === view.callId);
      if (this.view?.callId !== view.callId || this.view.phase === 'ended') return;
      if (call && call.state === 'ended') this.finish(call.endReason ?? 'hangup');
    } catch {
      /* offline: asked again on reconnect */
    }
  }

  /** Tells the server the call is over: over the socket, and over HTTP too (with one retry) in case it is down. */
  private endOnServer(callId: string, kind: 'hangup' | 'reject' | 'failed') {
    this.rt.command(kind === 'reject' ? 'call.reject' : 'call.hangup', { callId, ...(kind === 'reject' ? {} : { reason: kind }) });
    const send = () => api('POST', `/calls/${callId}/end`, { kind });
    void send().catch(() => setTimeout(() => void send().catch(() => undefined), 2000));
  }

  /** A call of mine running on another device (asked when this one connects or comes to the front). */
  private async checkElsewhere(): Promise<void> {
    try {
      const { call } = await api<{ call: (CallElsewhere & { peer: { id: string; displayName: string } }) | null }>('GET', '/calls/active');
      this.setElsewhere(call && call.callId !== this.view?.callId ? { ...call, peer: { id: call.peer.id, displayName: call.peer.displayName } } : null);
    } catch {
      /* offline: the realtime note will tell */
    }
  }

  private setElsewhere(next: CallElsewhere | null) {
    if (this.elsewhere?.callId === next?.callId) return;
    this.elsewhere = next;
    this.listeners.forEach((l) => l());
  }

  /**
   * Takes my call over from my other device: a fresh connection from here; the other person answers it and
   * the device that had the call lets go. The call keeps its start time; nobody rings.
   */
  async takeOver(): Promise<void> {
    const call = this.elsewhere;
    if (!call || this.busy()) return;
    this.setElsewhere(null);
    this.moving = call.callId;
    this.view = {
      callId: call.callId,
      conversationId: call.conversationId,
      peer: call.peer,
      media: call.media,
      phase: 'connecting',
      muted: false,
      cameraOff: false,
      connectedAt: Date.now(),
      endReason: null,
      localStream: null,
      remoteStream: null,
      ...this.extras(),
    };
    this.listeners.forEach((l) => l());
    if (!callsSupported()) return this.finish('unsupported');
    let stream: MediaStream;
    try {
      stream = await this.media(call.media === 'video');
    } catch {
      this.moving = null;
      return this.finish('permission');
    }
    this.hold(true);
    this.polite = false;
    this.update({ localStream: stream });
    try {
      const pc = await this.connection();
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      if (!stream.getVideoTracks().length) pc.addTransceiver('video', { direction: 'sendrecv', streams: [stream] });
      this.update({ canShare: true });
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      this.rt.command('call.move', { callId: call.callId, sdp: offer.sdp ?? '', device: thisDevice() }, call.callId);
      this.startConnectTimer();
    } catch {
      this.moving = null;
      this.finish('failed');
    }
  }

  /** The other person moved the call to another device: answer their new offer on a fresh connection. */
  private async answerMoved(callId: string, sdp: string): Promise<void> {
    const view = this.view;
    if (!view || view.callId !== callId || view.phase === 'ended' || view.phase === 'incoming') return;
    const stream = view.localStream;
    // A shared screen does not carry over; the camera and microphone do.
    if (view.screenStream) await this.stopScreenShare().catch(() => undefined);
    const old = this.pc;
    this.pc = null;
    this.channel = null;
    old?.close();
    this.remote = [];
    this.pendingIce = [];
    this.update({ phase: 'connecting', remoteStream: null, remoteAudio: null, peerState: NO_PEER_STATE });
    this.polite = true;
    try {
      const pc = await this.connection();
      await pc.setRemoteDescription({ type: 'offer', sdp });
      const tracks = stream?.getTracks() ?? [];
      // The sent picture may be an effect canvas rather than the raw camera: reuse what was being sent.
      const sentVideo = old?.getSenders().find((s) => s.track?.kind === 'video')?.track ?? null;
      for (const t of tracks) {
        const track = t.kind === 'video' && sentVideo ? sentVideo : t;
        if (stream) pc.addTrack(track, stream);
      }
      const videoLine = this.videoTransceiver();
      if (videoLine && stream) {
        videoLine.direction = 'sendrecv';
        videoLine.sender.setStreams?.(stream);
      }
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      this.rt.command('call.move_answer', { callId, sdp: answer.sdp ?? '', device: thisDevice() }, callId);
      this.startConnectTimer();
      await this.flushIce();
    } catch {
      this.hangup('failed');
    }
  }

  private async checkStillRinging(): Promise<void> {
    const view = this.view;
    if (view?.phase !== 'incoming') return;
    try {
      const { calls } = await api<{ calls: CallDto[] }>('GET', `/calls?conversationId=${view.conversationId}`);
      const call = calls.find((c) => c.id === view.callId);
      if (this.view?.callId !== view.callId || this.view.phase !== 'incoming') return;
      if (!call || call.state !== 'ringing') this.finish(call?.endReason ?? 'missed');
    } catch {
      /* offline: the ring timer still ends it */
    }
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private update(patch: Partial<CallView>) {
    if (!this.view) return;
    this.view = { ...this.view, ...patch };
    this.listeners.forEach((l) => l());
  }

  private async connection(): Promise<RTCPeerConnection> {
    const ice = await api<IceServersDto>('GET', '/calls/ice-servers');
    this.relayOnly = ice.relayOnly;
    this.reported = false;
    const pc = new RTCPeerConnection({
      iceServers: ice.iceServers,
      // Relay-only hides each side's IP address from the other (media goes through TURN).
      iceTransportPolicy: ice.relayOnly ? 'relay' : 'all',
    });
    pc.onicecandidate = (e) => {
      if (e.candidate && this.view) this.rt.command('call.ice', { callId: this.view.callId, candidate: e.candidate.toJSON() });
    };
    pc.ontrack = (e) => {
      // One stream for everything the other side sends: a video line added for screen sharing may come
      // without a stream of its own and must not replace the voice.
      if (!this.remote.includes(e.track)) this.remote.push(e.track);
      // The voice gets a stream of its own, made once per connection: its audio element is never reset.
      const voice = e.track.kind === 'audio' && !this.view?.remoteAudio?.getTracks().includes(e.track) ? new MediaStream([e.track]) : null;
      this.update({ remoteStream: new MediaStream(this.remote), ...(voice ? { remoteAudio: voice } : {}) });
    };
    const channel = pc.createDataChannel('state', { negotiated: true, id: 0 });
    channel.onopen = () => this.sendState();
    channel.onmessage = (e) => {
      try {
        const m = JSON.parse(String(e.data)) as Partial<PeerState> & { t?: string };
        // The other side hung up: end at once, even if the server's note is late or lost.
        if (m.t === 'bye') {
          if (this.channel === channel && this.view && this.view.phase !== 'ended') this.finish('hangup');
          return;
        }
        if (m.t !== 'state') return;
        this.update({ peerState: { muted: m.muted === true, cameraOff: m.cameraOff === true, sharing: m.sharing === true } });
      } catch {
        /* not ours */
      }
    };
    // The other side's connection closed (hung up, tab closed, or moved to another device): a moment later,
    // unless a move replaced this connection, ask the server whether the call is still on.
    channel.onclose = () => {
      setTimeout(() => {
        if (this.pc === pc && this.view && this.view.phase !== 'ended') void this.checkStillOn();
      }, 3000);
    };
    this.channel = channel;
    this.everConnected = false;
    pc.onconnectionstatechange = () => {
      if (!this.view || this.pc !== pc) return;
      if (pc.connectionState === 'connected') {
        this.clearConnectTimer();
        this.everConnected = true;
        this.update({ phase: 'active', connectedAt: this.view.connectedAt ?? Date.now(), reconnecting: false });
        this.startWatchdog();
        void this.report(pc, 'connected');
      }
      if (pc.connectionState === 'failed') {
        // A call that was talking gets a fresh path (a few tries); one that never connected ends.
        if (this.everConnected && this.view.phase === 'active' && this.restarts < 8) {
          this.lastRestart = 0;
          void this.restartIce();
        } else void this.failMedia(pc, 'ice_failed');
      }
    };
    pc.oniceconnectionstatechange = () => {
      if (!this.view || this.pc !== pc) return;
      const state = pc.iceConnectionState;
      if (state === 'disconnected') {
        // Often back within a second or two by itself; otherwise restart rather than stay silent.
        if (this.view.phase === 'active' && !this.view.reconnecting) {
          this.reconnectingSince = Date.now();
          this.update({ reconnecting: true });
        }
        if (this.disconnectTimer) clearTimeout(this.disconnectTimer);
        this.disconnectTimer = setTimeout(() => {
          if (this.pc === pc && (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed')) void this.restartIce();
        }, DISCONNECTED_GRACE_MS);
      } else if (state === 'connected' || state === 'completed') {
        if (this.disconnectTimer) clearTimeout(this.disconnectTimer);
        this.disconnectTimer = null;
        if (this.view.reconnecting) this.update({ reconnecting: false });
      }
    };
    this.pc = pc;
    return pc;
  }

  private audioConstraints(): MediaTrackConstraints {
    const s = this.settings;
    return {
      echoCancellation: s.echoCancellation,
      noiseSuppression: s.noiseSuppression,
      autoGainControl: s.autoGainControl,
      ...(s.micId ? { deviceId: { ideal: s.micId } } : {}),
    };
  }

  private videoConstraints(facing?: 'user' | 'environment'): MediaTrackConstraints {
    return {
      width: { ideal: 1280 },
      height: { ideal: 720 },
      ...(facing ? { facingMode: { ideal: facing } } : this.settings.camId ? { deviceId: { ideal: this.settings.camId } } : {}),
    };
  }

  /** Microphone (and camera) with the remembered devices and processing; effects applied to the camera. */
  private async media(video: boolean): Promise<MediaStream> {
    const raw = await navigator.mediaDevices.getUserMedia({ audio: this.audioConstraints(), video: video ? this.videoConstraints() : false });
    const audio = raw.getAudioTracks();
    audio.forEach((t) => this.watchMic(t));
    const cam = raw.getVideoTracks()[0] ?? null;
    this.camTrack = cam;
    if (!cam) return raw;
    await this.applyDeviceBlur(cam, this.settings.deviceBlur);
    return new MediaStream([...audio, this.videoOut(cam)]);
  }

  /** The track to send for a camera track: itself, or the effect canvas drawing it. */
  private videoOut(cam: MediaStreamTrack): MediaStreamTrack {
    this.processor?.stop();
    this.processor = null;
    if (!this.needsProcessor()) return cam;
    try {
      this.processor = new VideoEffectProcessor(cam, this.settings.effect);
      void this.applyBackground();
      return this.processor.output;
    } catch {
      return cam;
    }
  }

  private async applyDeviceBlur(cam: MediaStreamTrack, on: boolean): Promise<boolean> {
    const available = Boolean((cam.getCapabilities?.() as BlurCapable | undefined)?.backgroundBlur?.includes(true));
    if (available) await cam.applyConstraints({ advanced: [{ backgroundBlur: on } as MediaTrackConstraintSet] }).catch(() => undefined);
    if (this.view) this.update({ deviceBlur: { available, on: available && on } });
    return available;
  }

  /** Answered: media has CONNECT_TIMEOUT_MS to start flowing. */
  private startConnectTimer() {
    this.clearConnectTimer();
    this.connectStarted = Date.now();
    const pc = this.pc;
    this.connectTimer = setTimeout(() => {
      if (pc && this.pc === pc && this.view && this.view.phase !== 'active' && this.view.phase !== 'ended') void this.failMedia(pc, 'timeout');
    }, CONNECT_TIMEOUT_MS);
  }

  private clearConnectTimer() {
    if (this.connectTimer) clearTimeout(this.connectTimer);
    this.connectTimer = null;
  }

  /** Tells the server what this device saw (candidate kinds, relay transport; never addresses). */
  private async report(pc: RTCPeerConnection, outcome: 'connected' | 'failed', reason?: CallReport['reason']) {
    const callId = this.view?.callId;
    if (!callId || this.reported) return;
    this.reported = true;
    const facts = await connectionFacts(pc);
    const body: CallReport = {
      outcome,
      // Safari does not say how it reached the relay: a relay candidate is enough to know it got one.
      ...(reason ? { reason: facts.relayProtocols.length || facts.localTypes.includes('relay') || !this.relayOnly ? reason : 'no_relay' } : {}),
      ...facts,
      relayOnly: this.relayOnly,
      ms: Math.min(600_000, Math.max(0, Date.now() - (this.connectStarted || Date.now()))),
    };
    await api('POST', `/calls/${callId}/report`, body).catch(() => undefined);
  }

  /** Answered, but no media path: end the call for both and say why. */
  private async failMedia(pc: RTCPeerConnection, reason: 'ice_failed' | 'timeout') {
    if (!this.view || this.view.phase === 'ended') return;
    const neverConnected = this.view.connectedAt === null;
    if (neverConnected) await this.report(pc, 'failed', reason);
    // The call may have ended while the statistics were read.
    const view = this.view as CallView | null;
    if (!view || view.phase === 'ended') return;
    this.rt.command('call.hangup', { callId: view.callId, reason: 'failed' });
    // A call that was talking and then lost its path ended "disconnected"; one that never got media, "no_media".
    this.finish(neverConnected ? 'no_media' : 'failed');
  }

  private async flushIce() {
    const pc = this.pc;
    if (!pc?.remoteDescription || this.negotiating) return;
    for (const c of this.pendingIce.splice(0)) await pc.addIceCandidate(c).catch(() => undefined);
  }

  private busy() {
    return this.view !== null && this.view.phase !== 'ended';
  }

  /** Places a call to the peer of a direct conversation. */
  async start(conversationId: string, peer: CallView['peer'], media: CallMedia): Promise<void> {
    if (this.busy()) return;
    const callId = uuidv7();
    this.view = { callId, conversationId, peer, media, phase: 'outgoing', muted: false, cameraOff: false, connectedAt: null, endReason: null, localStream: null, remoteStream: null, ...this.extras() };
    this.listeners.forEach((l) => l());
    if (!callsSupported()) return this.finish('unsupported');
    let stream: MediaStream;
    try {
      stream = await this.media(media === 'video');
    } catch {
      return this.finish('permission');
    }
    if (this.view?.callId !== callId) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    this.hold(true);
    this.polite = false;
    this.update({ localStream: stream });
    try {
      const pc = await this.connection();
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      // A voice call still carries an (empty) video line, so either side can share a screen later
      // without renegotiating. Same stream id: older apps keep the voice and the picture together.
      if (!stream.getVideoTracks().length) pc.addTransceiver('video', { direction: 'sendrecv', streams: [stream] });
      this.update({ canShare: true });
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      this.rt.command('call.invite', { callId, conversationId, media, sdp: offer.sdp ?? '', device: thisDevice() }, callId);
    } catch {
      this.finish('failed');
    }
  }

  /** Answers the ringing call; `withVideo` false answers a video call with audio only. */
  async accept(withVideo = true): Promise<void> {
    const view = this.view;
    if (!view || view.phase !== 'incoming' || !this.incomingOffer) return;
    this.stopRinging();
    this.update({ phase: 'connecting' });
    let stream: MediaStream;
    try {
      stream = await this.media(view.media === 'video' && withVideo);
    } catch {
      this.rt.command('call.reject', { callId: view.callId });
      return this.finish('permission');
    }
    this.hold(true);
    this.polite = true;
    this.update({ localStream: stream, cameraOff: view.media === 'video' && !withVideo });
    try {
      const pc = await this.connection();
      await pc.setRemoteDescription({ type: 'offer', sdp: this.incomingOffer });
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      // Answering without a camera (or a voice call): keep the video line open for sending a screen.
      const videoLine = this.videoTransceiver();
      if (videoLine) {
        videoLine.direction = 'sendrecv';
        videoLine.sender.setStreams?.(stream);
      }
      this.update({ canShare: Boolean(videoLine) });
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      this.rt.command('call.answer', { callId: view.callId, sdp: answer.sdp ?? '', device: thisDevice() }, view.callId);
      this.startConnectTimer();
      await this.flushIce();
    } catch {
      this.hangup('failed');
    }
  }

  reject(): void {
    if (!this.view) return;
    this.endOnServer(this.view.callId, 'reject');
    this.finish('rejected');
  }

  hangup(reason: 'hangup' | 'failed' = 'hangup'): void {
    if (!this.view || this.view.phase === 'ended') return;
    // Straight to the other device first (fastest), then the server.
    if (this.channel?.readyState === 'open') {
      try {
        this.channel.send(JSON.stringify({ t: 'bye' }));
      } catch {
        /* closing anyway */
      }
    }
    this.endOnServer(this.view.callId, reason);
    this.finish(reason === 'failed' ? 'failed' : this.view.phase === 'outgoing' ? 'cancelled' : 'hangup');
  }

  toggleMute(): void {
    const muted = !this.view?.muted;
    this.view?.localStream?.getAudioTracks().forEach((t) => (t.enabled = !muted));
    this.update({ muted });
    this.sendState();
  }

  toggleCamera(): void {
    const cameraOff = !this.view?.cameraOff;
    this.view?.localStream?.getVideoTracks().forEach((t) => (t.enabled = !cameraOff));
    this.update({ cameraOff });
    this.sendState();
  }

  private sendState(): void {
    const v = this.view;
    if (!v || this.channel?.readyState !== 'open') return;
    this.channel.send(JSON.stringify({ t: 'state', muted: v.muted, cameraOff: v.cameraOff || v.media !== 'video', sharing: Boolean(v.screenStream) }));
  }

  private videoTransceiver(): RTCRtpTransceiver | null {
    return this.pc?.getTransceivers().find((t) => t.receiver.track.kind === 'video' && t.currentDirection !== 'stopped') ?? null;
  }

  /**
   * Sends my screen, a window or a browser tab instead of the camera (in voice calls too). The browser
   * asks which one; its own "stop sharing" bar ends it as well. Tab or system sound goes along when chosen.
   */
  async startScreenShare(): Promise<void> {
    const view = this.view;
    const sender = this.videoTransceiver()?.sender;
    if (!view || view.screenStream || !sender || !screenShareSupported()) return;
    const display = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: { ideal: 15, max: 30 }, width: { max: 1920 }, height: { max: 1080 } },
      audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      // Chrome: never offer this very tab (an endless mirror), keep the call's own sound out of the capture.
      selfBrowserSurface: 'exclude',
      surfaceSwitching: 'include',
      systemAudio: 'include',
      restrictOwnAudio: true,
    } as DisplayMediaStreamOptions);
    const track = display.getVideoTracks()[0];
    if (!track || this.view?.callId !== view.callId || this.view.phase === 'ended') {
      display.getTracks().forEach((t) => t.stop());
      return;
    }
    // Text on a screen must stay sharp: lower the frame rate before the resolution.
    track.contentHint = 'detail';
    track.addEventListener('ended', () => void this.stopScreenShare());
    await sender.replaceTrack(track);
    try {
      const params = sender.getParameters();
      params.degradationPreference = 'maintain-resolution';
      await sender.setParameters(params);
    } catch {
      /* not supported everywhere */
    }
    const sound = display.getAudioTracks()[0];
    if (sound) await this.mixIn(sound);
    this.update({ screenStream: display });
    this.sendState();
  }

  async stopScreenShare(): Promise<void> {
    const view = this.view;
    const screen = view?.screenStream;
    if (!view || !screen) return;
    screen.getTracks().forEach((t) => t.stop());
    this.update({ screenStream: null });
    // Back to the camera picture (as it was: on or off), or nothing in a voice call.
    await this.videoTransceiver()?.sender.replaceTrack(this.view?.localStream?.getVideoTracks()[0] ?? null).catch(() => undefined);
    await this.unmix();
    this.sendState();
  }

  private async mixIn(display: MediaStreamTrack): Promise<void> {
    const mic = this.view?.localStream?.getAudioTracks()[0];
    const sender = this.pc?.getSenders().find((s) => s.track?.kind === 'audio');
    if (!mic || !sender) return;
    try {
      const ctx = new AudioContext();
      const dest = ctx.createMediaStreamDestination();
      // The mic source follows the mic track's `enabled`, so mute keeps working while mixed.
      ctx.createMediaStreamSource(new MediaStream([mic])).connect(dest);
      ctx.createMediaStreamSource(new MediaStream([display])).connect(dest);
      const out = dest.stream.getAudioTracks()[0]!;
      await sender.replaceTrack(out);
      this.mix = { ctx, display, out };
    } catch {
      this.mix = null; // the screen still goes out, only without its sound
    }
  }

  private async unmix(): Promise<void> {
    const mix = this.mix;
    if (!mix) return;
    this.mix = null;
    const mic = this.view?.localStream?.getAudioTracks()[0] ?? null;
    await this.pc?.getSenders().find((s) => s.track === mix.out)?.replaceTrack(mic).catch(() => undefined);
    mix.out.stop();
    void mix.ctx.close();
  }

  private extras(): Pick<
    CallView,
    'effect' | 'deviceBlur' | 'speakerId' | 'background' | 'backgroundStatus' | 'screenStream' | 'canShare' | 'peerState' | 'peerDevice' | 'reconnecting' | 'silent' | 'remoteAudio' | 'peerVolume'
  > {
    return {
      effect: this.settings.effect,
      deviceBlur: { available: false, on: false },
      speakerId: this.settings.speakerId,
      background: this.settings.background,
      backgroundStatus: 'idle',
      screenStream: null,
      canShare: false,
      peerState: NO_PEER_STATE,
      peerDevice: null,
      reconnecting: false,
      silent: false,
      remoteAudio: null,
      peerVolume: 1,
    };
  }

  private needsProcessor(): boolean {
    return this.settings.effect !== 'none' || this.settings.background !== 'none';
  }

  private remember(patch: Partial<CallSettings>): void {
    this.settings = { ...this.settings, ...patch };
    saveCallSettings(this.settings);
  }

  /** Puts a new track into the call without renegotiating (the other side sees/hears the change at once). */
  private async swapTrack(kind: 'audio' | 'video', next: MediaStreamTrack): Promise<void> {
    const view = this.view;
    if (!view?.localStream) return;
    const sender =
      this.pc?.getSenders().find((s) => s.track?.kind === kind) ??
      this.pc?.getTransceivers().find((t) => t.receiver.track.kind === kind && t.currentDirection !== 'stopped' && !t.sender.track)?.sender;
    if (kind === 'audio') this.watchMic(next);
    // While a screen goes out, a new camera waits in the background; a new mic is mixed with the screen's sound.
    if (sender && !(kind === 'video' && view.screenStream) && !(kind === 'audio' && this.mix)) await sender.replaceTrack(next);
    const old = kind === 'audio' ? view.localStream.getAudioTracks() : view.localStream.getVideoTracks();
    if (kind === 'audio') next.enabled = !view.muted;
    else next.enabled = !view.cameraOff;
    const stream = new MediaStream([...(kind === 'audio' ? [next] : view.localStream.getAudioTracks()), ...(kind === 'video' ? [next] : view.localStream.getVideoTracks())]);
    // The old camera track may be the effect canvas: its source is handled by the caller.
    for (const t of old) if (t !== next && t !== this.camTrack) t.stop();
    this.update({ localStream: stream });
    if (kind === 'audio' && this.mix) {
      const display = this.mix.display;
      await this.unmix();
      await this.mixIn(display);
    }
  }

  async setMicrophone(deviceId: string): Promise<void> {
    this.remember({ micId: deviceId });
    if (!this.view?.localStream) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: this.audioConstraints() });
    await this.swapTrack('audio', stream.getAudioTracks()[0]!);
  }

  /** A camera by id, or (phones) the front/back camera. */
  async setCamera(choice: { deviceId: string } | { facing: 'user' | 'environment' }): Promise<void> {
    if ('deviceId' in choice) this.remember({ camId: choice.deviceId });
    if (!this.view?.localStream || this.view.media !== 'video') return;
    const old = this.camTrack;
    old?.stop(); // phones often allow only one open camera
    const stream = await navigator.mediaDevices.getUserMedia({ video: this.videoConstraints('facing' in choice ? choice.facing : undefined) });
    const cam = stream.getVideoTracks()[0]!;
    this.camTrack = cam;
    await this.applyDeviceBlur(cam, this.settings.deviceBlur);
    await this.swapTrack('video', this.videoOut(cam));
  }

  setSpeaker(deviceId: string): void {
    this.remember({ speakerId: deviceId });
    this.update({ speakerId: deviceId });
  }

  /** Noise suppression, echo cancellation, automatic gain: applied to the live microphone. */
  async setAudioProcessing(patch: Partial<Pick<CallSettings, 'noiseSuppression' | 'echoCancellation' | 'autoGainControl'>>): Promise<void> {
    this.remember(patch);
    const track = this.view?.localStream?.getAudioTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints(this.audioConstraints());
    } catch {
      // Some browsers only take these when the microphone is opened again.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: this.audioConstraints() });
      await this.swapTrack('audio', stream.getAudioTracks()[0]!);
    }
  }

  /** Keeps the drawing canvas when one is still needed, otherwise swaps between it and the plain camera. */
  private async refreshVideo(): Promise<void> {
    const cam = this.camTrack;
    if (!cam || !this.view?.localStream) return;
    if (this.processor && this.needsProcessor()) {
      this.processor.setEffect(this.settings.effect);
      await this.applyBackground();
      return;
    }
    await this.swapTrack('video', this.videoOut(cam));
  }

  async setEffect(effect: VideoEffect): Promise<void> {
    this.remember({ effect });
    this.update({ effect });
    await this.refreshVideo();
  }

  async setBackground(background: BackgroundChoice): Promise<void> {
    this.remember({ background });
    this.update({ background });
    await this.refreshVideo();
  }

  /** A picture from the device becomes the background (stored on this device only). */
  async setCustomBackground(file: Blob): Promise<void> {
    this.bitmaps.set('custom', await saveCustomBackground(file));
    await this.setBackground('custom');
  }

  private async backgroundLayer(choice: BackgroundChoice): Promise<BackgroundLayer> {
    if (choice === 'none') return { kind: 'none' };
    if (choice === 'blur' || choice === 'blur-strong') return { kind: 'blur', radius: choice === 'blur' ? 8 : 18 };
    let image = this.bitmaps.get(choice) ?? null;
    if (!image) {
      image = choice === 'custom' ? await loadCustomBackground() : await presetBackground(choice as BackgroundPreset);
      if (image) this.bitmaps.set(choice, image);
    }
    return image ? { kind: 'image', image } : { kind: 'none' };
  }

  /** Loads the person detection on first use, then hands the processor its background. */
  private async applyBackground(): Promise<void> {
    const processor = this.processor;
    const choice = this.settings.background;
    if (!processor) return;
    if (choice === 'none') {
      processor.setBackground({ kind: 'none' }, null);
      if (this.view) this.update({ backgroundStatus: 'idle' });
      return;
    }
    if (this.view) this.update({ backgroundStatus: 'loading' });
    try {
      const [segmenter, layer] = await Promise.all([loadSegmenter(), this.backgroundLayer(choice)]);
      // Something else may have been picked meanwhile.
      if (this.processor !== processor || this.settings.background !== choice) return;
      processor.setBackground(layer, segmenter);
      if (this.view) this.update({ backgroundStatus: 'ready' });
    } catch {
      processor.setBackground({ kind: 'none' }, null);
      if (this.view) this.update({ backgroundStatus: 'failed' });
    }
  }

  async setDeviceBlur(on: boolean): Promise<void> {
    this.remember({ deviceBlur: on });
    if (this.camTrack) await this.applyDeviceBlur(this.camTrack, on);
  }

  /** The camera picture as it comes from the device (for previews of effects). */
  get cameraTrack(): MediaStreamTrack | null {
    return this.camTrack;
  }

  dismiss(): void {
    if (this.view?.phase === 'ended') {
      this.view = null;
      this.listeners.forEach((l) => l());
    }
  }

  private finish(reason: CallFailure) {
    if (this.view && this.view.phase !== 'ended' && this.everConnected) this.sendQuality(this.view.callId, this.view.connectedAt);
    this.moving = null;
    this.clearConnectTimer();
    this.stopWatchdog();
    if (this.disconnectTimer) clearTimeout(this.disconnectTimer);
    if (this.offerTimer) clearTimeout(this.offerTimer);
    this.disconnectTimer = this.offerTimer = null;
    this.restarts = 0;
    this.lastRestart = 0;
    this.lastRecovery = 0;
    this.noAudioSeen = false;
    this.snapshot = null;
    this.micPending = false;
    this.everConnected = false;
    this.negotiating = false;
    this.hold(false);
    this.stopRinging();
    // A call that rang here and was not answered: "1 missed call" stays in the tab title until I look.
    if (this.view?.phase === 'incoming' && (reason === 'missed' || reason === 'cancelled')) addMissedCall(`📞 ${this.text.missedFrom(this.view.peer.displayName)}`);
    this.view?.screenStream?.getTracks().forEach((t) => t.stop());
    if (this.mix) {
      this.mix.out.stop();
      void this.mix.ctx.close();
      this.mix = null;
    }
    this.channel = null;
    this.remote = [];
    this.pc?.close();
    this.pc = null;
    this.pendingIce = [];
    this.incomingOffer = null;
    this.view?.localStream?.getTracks().forEach((t) => t.stop());
    this.camTrack?.stop();
    this.camTrack = null;
    this.processor?.stop();
    this.processor = null;
    this.update({ phase: 'ended', endReason: reason, localStream: null, remoteStream: null, remoteAudio: null, screenStream: null, peerState: NO_PEER_STATE, reconnecting: false, silent: false });
    if (this.clearTimer) clearTimeout(this.clearTimer);
    // A failed connection carries advice (try another network): it stays long enough to read.
    this.clearTimer = setTimeout(() => this.dismiss(), reason === 'no_media' ? 15_000 : 4000);
  }

  private stopAlert: (() => void) | null = null;

  /** Rings like a phone; if the app is not in front, also a system notification and a flashing tab title. */
  private startRinging() {
    const view = this.view;
    // The ringtone chosen for voice or video calls (Settings › Notifications › Sounds).
    this.ringer = ringtone(view?.media === 'video' ? 'video' : 'call');
    if (!view) return;
    const title = view.media === 'video' ? this.text.incomingVideo(view.peer.displayName) : this.text.incomingVoice(view.peer.displayName);
    this.stopAlert = flashTitle(`📞 ${title}`);
    if (lookingElsewhere(null, null)) {
      void showLocal(title, { body: this.text.tapToAnswer, tag: `call-${view.callId}`, url: `/messages/${view.conversationId}`, requireInteraction: true });
    }
  }

  private stopRinging() {
    if (this.ringTimer) clearTimeout(this.ringTimer);
    this.ringTimer = null;
    this.ringer?.stop();
    this.ringer = null;
    this.stopAlert?.();
    this.stopAlert = null;
    if (this.view) void closeLocal(`call-${this.view.callId}`);
  }

  /** Texts for call alerts, in the person's language (set by the app once translations are loaded). */
  text = {
    incomingVoice: (name: string) => `${name}`,
    incomingVideo: (name: string) => `${name}`,
    tapToAnswer: '',
    missedFrom: (name: string) => `${name}`,
  };

  /** Server events routed here by the chat store. */
  handle(e: ServerEvent): void {
    switch (e.event) {
      case 'call.incoming': {
        // The server tells a device that (re)connects about calls still ringing for it: once is enough here.
        if (this.view?.callId === e.payload.callId) return;
        // Delivered late (a frozen background tab replays what it buffered): that call is over.
        const ringUntil = Date.parse(e.payload.ringUntil);
        const ringsFor = Number.isFinite(ringUntil) ? ringUntil - Date.now() : CALL_RING_TIMEOUT_MS;
        if (ringsFor <= 0) return;
        // Already in a call on this device: the server marks the newcomer busy on its own only when the
        // user is busy everywhere, so decline it here.
        if (this.busy() || this.groupBusy()) {
          this.rt.command('call.reject', { callId: e.payload.callId });
          return;
        }
        this.incomingOffer = e.payload.sdp;
        this.view = {
          callId: e.payload.callId,
          conversationId: e.payload.conversationId,
          peer: { id: e.payload.caller.id, displayName: e.payload.caller.displayName },
          media: e.payload.media,
          phase: 'incoming',
          muted: false,
          cameraOff: false,
          connectedAt: null,
          endReason: null,
          localStream: null,
          remoteStream: null,
          ...this.extras(),
          peerDevice: e.payload.device ?? null,
        };
        this.listeners.forEach((l) => l());
        this.startRinging();
        const callId = e.payload.callId;
        // Tell the caller it really rings here (their screen switches from "Calling" to "Ringing").
        this.rt.command('call.ringing', { callId });
        // A little after the server's own timeout, so its "missed" normally arrives first.
        this.ringTimer = setTimeout(() => {
          if (this.view?.callId === callId && this.view.phase === 'incoming') this.finish('missed');
        }, Math.min(ringsFor, CALL_RING_TIMEOUT_MS) + 3000);
        return;
      }
      case 'call.answered':
        if (this.view?.callId === e.payload.callId && this.pc && (this.view.phase === 'outgoing' || (this.moving === e.payload.callId && !this.pc.remoteDescription))) {
          if (this.view.phase === 'outgoing') {
            this.update({ phase: 'connecting' });
            this.startConnectTimer();
          }
          this.moving = null;
          if (e.payload.device) this.update({ peerDevice: e.payload.device });
          void this.pc
            .setRemoteDescription({ type: 'answer', sdp: e.payload.sdp })
            .then(() => this.flushIce())
            .catch(() => this.hangup('failed'));
        }
        return;
      case 'call.ice':
        if (this.view?.callId !== e.payload.callId) return;
        if (this.pc?.remoteDescription && !this.negotiating) void this.pc.addIceCandidate(e.payload.candidate).catch(() => undefined);
        else this.pendingIce.push(e.payload.candidate);
        return;
      case 'call.upgraded':
        this.upgrade(e.payload.callId, { conversationId: e.payload.conversationId, title: e.payload.title, media: e.payload.media });
        return;
      case 'call.ringing':
        if (this.view?.callId === e.payload.callId && this.view.phase === 'outgoing') this.update({ ringing: true });
        return;
      case 'call.moved':
        if (this.view?.callId === e.payload.callId) this.update({ peerDevice: e.payload.device ?? null });
        void this.answerMoved(e.payload.callId, e.payload.sdp);
        return;
      case 'call.renegotiate':
        void this.onRenegotiate(e.payload.callId, e.payload.sdp);
        return;
      case 'call.renegotiated':
        void this.onRenegotiated(e.payload.callId, e.payload.sdp);
        return;
      case 'call.elsewhere':
        if (!e.payload.active) {
          if (this.elsewhere?.callId === e.payload.callId) this.setElsewhere(null);
        } else if (this.view?.callId !== e.payload.callId || this.view.phase === 'ended') {
          this.setElsewhere({ callId: e.payload.callId, conversationId: e.payload.conversationId, peer: { id: e.payload.peer.id, displayName: e.payload.peer.displayName }, media: e.payload.media });
        }
        return;
      case 'call.ended':
        // The device taking the call over ignores the note about itself, but only until its new connection
        // is answered: after that a later "moved" (taken back by another device) is for it too.
        if (e.payload.reason === 'moved' && this.moving === e.payload.callId && !this.pc?.remoteDescription) return;
        if (this.elsewhere?.callId === e.payload.callId && e.payload.reason !== 'moved') this.setElsewhere(null);
        if (this.view?.callId !== e.payload.callId || this.view.phase === 'ended') return;
        // This device answered: the "answered elsewhere" note is for the user's other devices.
        if (e.payload.reason === 'answered_elsewhere' && this.view.phase !== 'incoming') return;
        // The other side could not get media through either: same explanation on both screens.
        if (e.payload.reason === 'failed' && this.view.phase === 'connecting' && this.pc) {
          void this.report(this.pc, 'failed', 'ice_failed');
          this.finish('no_media');
          return;
        }
        this.finish(e.payload.reason);
        return;
      case 'error':
        if (this.view && e.payload.reqId === this.view.callId && this.view.phase !== 'ended') {
          this.rt.command('call.hangup', { callId: this.view.callId });
          this.finish(e.payload.code === 'MESSAGING_NOT_ALLOWED' || e.payload.code === 'FORBIDDEN' ? 'not_allowed' : 'failed');
        }
        return;
      default:
        return;
    }
  }
}
